// Global constants shared by every module. Tuning values that belong to a single
// mech live in src/data/mechs.ts; tuning values that belong to one system live
// next to that system.

export const GAME_WIDTH = 1920;
export const GAME_HEIGHT = 1080;

/** World tile size in pixels. Room templates are authored on this grid. */
export const TILE = 48;

/** Mech animation frame cell (transparent), per the art bible. */
export const CELL = 128;
/** Foot anchor inside a CELL: the point that sits on the ground. Identical for every frame. */
export const FOOT_ANCHOR = { x: 64, y: 124 } as const;

/** Movement collider. Stays constant across armour changes (decorative armour never changes hitbox). */
export const PLAYER_BODY = { w: 40, h: 88 } as const;

/** Largest simulation step; frames slower than this are simulated in slow motion instead of tunnelling. */
export const MAX_DT = 1 / 30;

export const SECTORS_PER_RUN = 3;

/** Camera-speed multipliers for the five parallax layers (sky → foreground). */
export const PARALLAX = {
  sky: 0.03,
  far: 0.12,
  mid: 0.3,
  near: 0.6,
  fore: 1.1,
} as const;
export type ParallaxLayer = keyof typeof PARALLAX;
export const PARALLAX_LAYERS: ParallaxLayer[] = ['sky', 'far', 'mid', 'near', 'fore'];

/** Render depths inside GameScene. UI lives in its own scene above everything. */
export const DEPTH = {
  BG_SKY: -100,
  BG_FAR: -90,
  BG_MID: -80,
  BG_NEAR: -70,
  WORLD_BACK: -20, // decor behind tiles (signs, pipes)
  ZONES: -5, // ground-hugging zones (burn trails, fields)
  TILES: 0,
  PROPS: 5, // doors, crates, terminals, station
  PICKUPS: 8, // orbs, item holograms, scrap
  ENEMIES: 10,
  BOSS: 12,
  PLAYER: 20,
  PROJECTILES: 30,
  FX: 40,
  FX_ADD: 45, // additive glow FX
  FOREGROUND: 60, // parallax foreground layer, rain
  WORLD_UI: 70, // in-world prompts, item comparison cards, damage numbers
  TELEGRAPH: 65, // enemy warning shapes (above foreground so they are never hidden)
} as const;

export const SCENES = {
  BOOT: 'Boot',
  MENU: 'Menu',
  HANGAR: 'Hangar',
  GAME: 'Game',
  UI: 'UI',
  REWARD: 'Reward',
  ROUTE: 'Route',
  STATION: 'Station',
  PAUSE: 'Pause',
  RESULTS: 'Results',
  SETTINGS: 'Settings',
  PREVIEW: 'AssetPreview',
} as const;
export type SceneKey = (typeof SCENES)[keyof typeof SCENES];

/** CSS font stacks. Fonts are bundled in public/fonts and loaded in index.html. */
export const FONT = {
  display: '"Chakra Petch", "Bahnschrift", "Segoe UI", sans-serif',
  body: '"Rajdhani", "Bahnschrift", "Segoe UI", sans-serif',
  mono: '"JetBrains Mono", Consolas, monospace',
} as const;

/** Player-facing ultimate cost in charge points. */
export const ULTIMATE_COST = 100;

/** Seconds of invulnerability after the player takes a hit. */
export const PLAYER_HIT_IFRAMES = 0.6;
